const headingPattern = /^[A-Z0-9][A-Z0-9 +&/()-]{2,60}$/;
const markdownHeadingPattern = /^\*\*(.{2,80})\*\*$/;
const urlPattern =
  /https?:\/\/[^\s<]+|\b(?:app|docs)\.nosible\.com(?:\/[^\s<]*)?/g;
const codeStyle =
  "font-family:Consolas,Monaco,'Courier New',monospace;font-size:14px;overflow-wrap:anywhere;word-break:break-all";

export const NOSIBLE_EMAIL_SIGNATURE_SLOT =
  "<!--nosible-email-signature-slot-->";

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function linkify(value: string): string {
  let result = "";
  let cursor = 0;
  for (const match of value.matchAll(urlPattern)) {
    const matchedUrl = match[0];
    const trailingPunctuation = matchedUrl.match(/[.,;:!?]+$/)?.[0] ?? "";
    const url = trailingPunctuation
      ? matchedUrl.slice(0, -trailingPunctuation.length)
      : matchedUrl;
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    const index = match.index ?? 0;
    result += escapeHtml(value.slice(cursor, index));
    result += `<a href="${escapeHtml(href)}" style="color:#087a49;font-weight:600;text-decoration:underline">${escapeHtml(url)}</a>`;
    result += escapeHtml(trailingPunctuation);
    cursor = index + matchedUrl.length;
  }
  return result + escapeHtml(value.slice(cursor));
}

function formatLine(value: string): string {
  const credential = value.match(
    /^(API Key|S3 Access Key|S3 Secret Key|Data Prefix|Date Folders):\s*(.+)$/i,
  );
  if (credential) {
    return `<strong>${escapeHtml(credential[1] ?? "")}:</strong> <code style="${codeStyle}">${escapeHtml(credential[2] ?? "")}</code>`;
  }

  const datasetFile = value.match(
    /^([a-z0-9_]+\.(?:ndjson|locs|hnsw|ipc|sqlite))\s+-\s+(.+)$/i,
  );
  if (datasetFile) {
    return `<code style="${codeStyle}">${escapeHtml(datasetFile[1] ?? "")}</code> - ${linkify(datasetFile[2] ?? "")}`;
  }

  const colon = value.indexOf(":");
  if (
    colon > 0 &&
    colon < 48 &&
    !value.slice(0, colon).includes("http") &&
    !value.startsWith("Authorization:")
  ) {
    return `<strong>${escapeHtml(value.slice(0, colon + 1))}</strong>${linkify(value.slice(colon + 1))}`;
  }
  return linkify(value);
}

type EmailListItem = {
  content: string;
  children: EmailListItem[];
};

function listDepth(indentation: string): number {
  const expanded = indentation.replace(/\t/g, "  ").length;
  return Math.min(6, Math.floor(expanded / 2));
}

function renderList(items: EmailListItem[], depth = 0): string {
  const style =
    depth === 0
      ? "margin:0 0 22px;padding-left:25px;list-style-type:disc;list-style-position:outside"
      : "margin:8px 0 2px;padding-left:25px;list-style-type:circle;list-style-position:outside";
  return `<ul style="${style}">${items
    .map(
      (item) =>
        `<li style="margin:0 0 7px;padding-left:2px">${formatLine(item.content)}${item.children.length ? renderList(item.children, depth + 1) : ""}</li>`,
    )
    .join("")}</ul>`;
}

function parseList(lines: string[], startIndex: number) {
  const roots: EmailListItem[] = [];
  const latestAtDepth: EmailListItem[] = [];
  let index = startIndex;

  while (index < lines.length) {
    const bullet = lines[index]?.match(/^(\s*)(?:\*|-)\s+(.+)$/);
    if (!bullet) break;

    let depth = listDepth(bullet[1] ?? "");
    while (depth > 0 && !latestAtDepth[depth - 1]) depth -= 1;

    const item: EmailListItem = {
      content: bullet[2] ?? "",
      children: [],
    };
    if (depth === 0) {
      roots.push(item);
    } else {
      latestAtDepth[depth - 1]!.children.push(item);
    }
    latestAtDepth[depth] = item;
    latestAtDepth.length = depth + 1;
    index += 1;
  }

  return {html: renderList(roots), nextIndex: index};
}

/**
 * Converts the deliberately simple onboarding email editor syntax into safe
 * email HTML. The editor accepts paragraphs, `**section headings**`,
 * uppercase legacy headings, checkboxes, `*`/`-` bullets, labels before a
 * colon, and web links. Everything else is escaped rather than interpreted
 * as HTML.
 */
export function renderEditableEmailHtml(body: string): string {
  const lines = body.replace(/\r\n?/g, "\n").split("\n");
  const output: string[] = [];

  for (let index = 0; index < lines.length; index += 1) {
    const rawLine = lines[index] ?? "";
    const trimmed = rawLine.trim();
    if (!trimmed) continue;

    if (/^(\s*)(?:\*|-)\s+(.+)$/.test(rawLine)) {
      const list = parseList(lines, index);
      output.push(list.html);
      index = list.nextIndex - 1;
      continue;
    }

    const markdownHeading = trimmed.match(markdownHeadingPattern);
    if (markdownHeading) {
      output.push(
        `<h2 style="margin:34px 0 16px;border-bottom:2px solid #26d07c;padding:0 0 9px;font-size:19px;line-height:1.3;font-weight:700;color:#111113">${escapeHtml(markdownHeading[1] ?? "")}</h2>`,
      );
      continue;
    }

    if (headingPattern.test(trimmed)) {
      output.push(
        `<h2 style="margin:34px 0 16px;border-bottom:2px solid #26d07c;padding:0 0 9px;font-size:19px;line-height:1.3;font-weight:700;color:#111113">${escapeHtml(trimmed)}</h2>`,
      );
      continue;
    }

    const checkbox = trimmed.match(/^\[([ xX])\]\s+(.+)$/);
    if (checkbox) {
      const checked = checkbox[1]?.toLowerCase() === "x";
      output.push(
        `<div style="margin:0 0 9px;line-height:1.45"><span style="display:inline-block;width:24px;color:${checked ? "#137333" : "#5f6368"};font-weight:700">${checked ? "&#9745;" : "&#9744;"}</span>${formatLine(checkbox[2] ?? "")}</div>`,
      );
      continue;
    }

    if (trimmed.startsWith("These credentials are confidential.")) {
      output.push(
        `<p style="margin:24px 0 0;padding:12px 14px;background-color:#fff9c4;border-left:4px solid #f1c40f">${formatLine(trimmed)}</p>`,
      );
      continue;
    }

    if (trimmed.startsWith("Please store these credentials")) {
      output.push(
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:30px 0;border-collapse:collapse;background-color:#fff3c4;border-left:5px solid #e6ad00"><tr><td style="padding:16px 18px"><div style="margin:0 0 5px;color:#765900;font-family:Consolas,Monaco,'Courier New',monospace;font-size:11px;font-weight:700;letter-spacing:.12em">SECURITY NOTE</div><div style="color:#3f3200;font-size:15px;line-height:1.55;font-weight:600">${formatLine(trimmed)}</div></td></tr></table>`,
      );
      continue;
    }

    output.push(
      `<p style="margin:0 0 18px">${formatLine(trimmed)}</p>`,
    );
  }

  return `<div style="margin:0;background-color:#f3f3f4;padding:24px 12px"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="width:100%;max-width:760px;margin:0 auto;border-collapse:collapse;border-top:4px solid #26d07c;background-color:#ffffff"><tr><td style="background-color:#0e0e10;padding:20px 28px"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse"><tr><td style="color:#ffffff;font-family:Arial,Helvetica,sans-serif;font-size:22px;font-weight:700;letter-spacing:.08em">NOSIBLE</td><td align="right" style="color:#9dffca;font-family:Consolas,Monaco,'Courier New',monospace;font-size:10px;font-weight:700;letter-spacing:.14em">TRIAL ACCESS</td></tr></table></td></tr><tr><td style="padding:34px 38px 38px;font-family:Arial,Helvetica,sans-serif;font-size:16px;line-height:1.6;color:#27272a">${output.join("\n")}</td></tr>${NOSIBLE_EMAIL_SIGNATURE_SLOT}</table></div>`;
}

/**
 * Places Gmail's configured signature inside the centered NOSIBLE email card.
 * Other HTML messages without the slot retain the previous append behavior.
 */
export function embedEmailSignatureHtml(
  emailHtml: string,
  signatureHtml: string,
): string {
  const signature = signatureHtml.trim();
  if (!signature) {
    return emailHtml.replace(NOSIBLE_EMAIL_SIGNATURE_SLOT, "");
  }

  if (!emailHtml.includes(NOSIBLE_EMAIL_SIGNATURE_SLOT)) {
    return `${emailHtml}<div style="margin-top:24px">${signature}</div>`;
  }

  const signatureRow = `<tr><td style="padding:0 28px 34px;font-family:Arial,Helvetica,sans-serif;color:#27272a"><div style="max-width:100%;border-top:1px solid #dedee2;padding-top:24px">${signature}</div></td></tr>`;
  return emailHtml.replace(NOSIBLE_EMAIL_SIGNATURE_SLOT, signatureRow);
}
